import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { differenceInDays, differenceInCalendarDays } from "date-fns";
import {
  Lightbulb,
  Share2,
  Target,
  Building2,
  Eye,
  TrendingUp,
  ArrowRight,
  X,
  Sparkles,
  Clock,
  Users,
  Heart,
  Play,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

interface WalletData {
  id: string;
  userId: string;
  patientName: string;
  category: string;
  description?: string;
  balance: number;
  totalContributions: number;
  totalSpent: number;
  goalAmount?: number;
  isPublic: boolean;
  shareToken: string;
  createdAt: string;
  walletType?: string;
  organizationName?: string;
}

interface Transaction {
  id: number;
  walletId: string;
  type: string;
  amount: number;
  description?: string;
  contributorName?: string;
  orderId?: string;
  status: string;
  createdAt: string;
}

interface PaymentPlan {
  id: string;
  walletId: string;
  hospitalName: string;
  originalBalance: number;
  remainingBalance: number;
  totalPaid: number;
  paymentAmount: number;
  frequency: string;
  status: string;
  isPaused: boolean;
  nextPaymentDate?: string;
}

type NudgePriority = "high" | "medium" | "low";
type NudgeVariant = "success" | "warning" | "info" | "action";

interface Nudge {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  priority: NudgePriority;
  variant: NudgeVariant;
  actionLabel?: string;
  actionHref?: string;
  onAction?: () => void;
  progress?: number;
  detail?: string;
}

interface SmartCTAsProps {
  wallets: WalletData[];
  transactions: Transaction[];
  paymentPlans: PaymentPlan[];
  employerEnrollment: any;
  onLinkEmployer?: () => void;
  pendingThankYouCount?: number;
  userCreatedAt?: string | Date;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function generateNudges(
  wallets: WalletData[],
  transactions: Transaction[],
  paymentPlans: PaymentPlan[],
  employerEnrollment: any,
  onLinkEmployer?: () => void,
  pendingThankYouCount?: number,
  userCreatedAt?: string | Date,
  t: (key: string, opts?: Record<string, unknown>) => string = (k) => k
): Nudge[] {
  const nudges: Nudge[] = [];
  const now = new Date();

  const activePlans = paymentPlans.filter(p => p.status === "active" || p.status === "paused");
  const pausedPlans = activePlans.filter(p => p.isPaused);

  if (wallets.length === 0) {
    const daysSinceRegistered = userCreatedAt
      ? differenceInDays(now, new Date(userCreatedAt))
      : 0;
    const isDay3Nudge = daysSinceRegistered >= 3;

    nudges.push({
      id: "no-wallet",
      title: isDay3Nudge
        ? t('smart-ctas.no-wallet.title-ready')
        : t('smart-ctas.no-wallet.title'),
      description: isDay3Nudge
        ? t('smart-ctas.no-wallet.body-day3')
        : t('smart-ctas.no-wallet.body'),
      icon: <Sparkles className="h-5 w-5" />,
      priority: "high",
      variant: "action",
      actionLabel: t('common.cta.create-wallet'),
      actionHref: "/create-wallet",
    });
    return nudges;
  }

  activePlans.forEach(plan => {
    if (!plan.isPaused && plan.nextPaymentDate) {
      const daysUntil = differenceInCalendarDays(new Date(plan.nextPaymentDate), now);
      if (daysUntil >= 0 && daysUntil <= 7) {
        const pctPaid = plan.originalBalance > 0 ? Math.round((plan.totalPaid / plan.originalBalance) * 100) : 0;
        nudges.push({
          id: `payment-due-${plan.id}`,
          title: daysUntil === 0
            ? t('smart-ctas.payment-due.title-today', { hospital: plan.hospitalName })
            : t('smart-ctas.payment-due.title-days', { count: daysUntil, hospital: plan.hospitalName }),
          description: t('smart-ctas.payment-due.description', {
            amount: formatCents(plan.paymentAmount),
            frequency: plan.frequency,
            remaining: formatCents(plan.remainingBalance),
          }),
          icon: <Clock className="h-5 w-5" />,
          priority: daysUntil <= 3 ? "high" : "medium",
          variant: daysUntil <= 1 ? "warning" : "info",
          actionLabel: t('common.cta.view-plan'),
          actionHref: "/payment-plans",
          progress: pctPaid,
          detail: t('smart-ctas.payment-due.percent-paid', { percent: pctPaid }),
        });
      }
    }
  });

  pausedPlans.forEach(plan => {
    nudges.push({
      id: `paused-plan-${plan.id}`,
      title: t('smart-ctas.paused-plan.title', { hospital: plan.hospitalName }),
      description: t('smart-ctas.paused-plan.description', { remaining: formatCents(plan.remainingBalance) }),
      icon: <Play className="h-5 w-5" />,
      priority: "medium",
      variant: "warning",
      actionLabel: t('common.cta.resume-plan'),
      actionHref: "/payment-plans",
    });
  });

  wallets.forEach(wallet => {
    if (wallet.goalAmount && wallet.goalAmount > 0) {
      const progress = Math.round((wallet.totalContributions / wallet.goalAmount) * 100);
      const remaining = wallet.goalAmount - wallet.totalContributions;
      const walletLabel = wallet.organizationName || wallet.patientName;

      if (progress >= 100) {
        nudges.push({
          id: `goal-reached-${wallet.id}`,
          title: t('smart-ctas.goal-reached.title', { name: walletLabel }),
          description: t('smart-ctas.goal-reached.description'),
          icon: <Target className="h-5 w-5" />,
          priority: "low",
          variant: "success",
          actionLabel: t('common.cta.view-wallet'),
          actionHref: `/wallet/${wallet.id}`,
          progress: 100,
        });
      } else if (progress >= 75) {
        const avgContribution = wallet.totalContributions > 0 && transactions.length > 0
          ? wallet.totalContributions / Math.max(1, transactions.filter(t => t.walletId === wallet.id && t.type === "contribution").length)
          : 0;
        const peopleNeeded = avgContribution > 0 ? Math.ceil(remaining / avgContribution) : 3;

        nudges.push({
          id: `almost-funded-${wallet.id}`,
          title: t('smart-ctas.almost-funded.title', { progress }),
          description: t('smart-ctas.almost-funded.description', {
            name: walletLabel,
            amount: formatCents(remaining),
            count: Math.max(1, Math.min(peopleNeeded, 10)),
          }),
          icon: <TrendingUp className="h-5 w-5" />,
          priority: "medium",
          variant: "success",
          actionLabel: t('common.cta.share-wallet'),
          actionHref: `/wallet/${wallet.id}`,
          progress,
          detail: t('smart-ctas.remaining-to-go', { amount: formatCents(remaining) }),
        });
      } else if (progress >= 25) {
        nudges.push({
          id: `funding-progress-${wallet.id}`,
          title: t('smart-ctas.funding-progress.title', { progress }),
          description: t('smart-ctas.funding-progress.description', {
            name: walletLabel,
            raised: formatCents(wallet.totalContributions),
            goal: formatCents(wallet.goalAmount),
          }),
          icon: <TrendingUp className="h-5 w-5" />,
          priority: "low",
          variant: "info",
          actionLabel: t('common.cta.share-wallet'),
          actionHref: `/wallet/${wallet.id}`,
          progress,
          detail: t('smart-ctas.remaining-to-go', { amount: formatCents(remaining) }),
        });
      }
    }
  });

  const privateWalletsWithGoals = wallets.filter(w => !w.isPublic && w.goalAmount && w.goalAmount > 0);
  if (privateWalletsWithGoals.length > 0) {
    const wallet = privateWalletsWithGoals[0];
    nudges.push({
      id: `make-public-${wallet.id}`,
      title: t('smart-ctas.boost-visibility.title'),
      description: t('smart-ctas.boost-visibility.body'),
      icon: <Eye className="h-5 w-5" />,
      priority: "low",
      variant: "action",
      actionLabel: t('common.cta.manage-wallet'),
      actionHref: `/wallet/${wallet.id}`,
    });
  }

  const walletsWithoutGoals = wallets.filter(w => !w.goalAmount || w.goalAmount === 0);
  if (walletsWithoutGoals.length > 0 && wallets.length <= 3) {
    const wallet = walletsWithoutGoals[0];
    nudges.push({
      id: `set-goal-${wallet.id}`,
      title: t('smart-ctas.set-goal.title'),
      description: t('smart-ctas.set-goal.body'),
      icon: <Target className="h-5 w-5" />,
      priority: "low",
      variant: "action",
      actionLabel: t('common.cta.set-goal'),
      actionHref: `/wallet/${wallet.id}`,
    });
  }

  if (!employerEnrollment && wallets.length > 0) {
    nudges.push({
      id: "link-employer",
      title: t('smart-ctas.link-employer.title'),
      description: t('smart-ctas.link-employer.body'),
      icon: <Building2 className="h-5 w-5" />,
      priority: "low",
      variant: "action",
      actionLabel: t('common.cta.link-employer'),
      onAction: onLinkEmployer,
    });
  }

  const walletContributionTxs = transactions.filter(t => t.type === "contribution");
  if (wallets.length > 0 && walletContributionTxs.length === 0) {
    nudges.push({
      id: "first-contribution",
      title: t('smart-ctas.first-contribution.title'),
      description: t('smart-ctas.first-contribution.body'),
      icon: <Share2 className="h-5 w-5" />,
      priority: "medium",
      variant: "action",
      actionLabel: t('common.cta.share-wallet'),
      actionHref: `/wallet/${wallets[0].id}`,
    });
  } else if (walletContributionTxs.length > 0) {
    const latestContribution = walletContributionTxs.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
    const daysSinceLastContribution = differenceInDays(now, new Date(latestContribution.createdAt));
    if (daysSinceLastContribution > 14) {
      nudges.push({
        id: "re-engage-contributors",
        title: t('smart-ctas.re-engage.title'),
        description: t('smart-ctas.re-engage.description', { days: daysSinceLastContribution }),
        icon: <Users className="h-5 w-5" />,
        priority: "low",
        variant: "info",
        actionLabel: t('common.cta.share-update'),
        actionHref: `/wallet/${wallets[0].id}`,
      });
    }
  }

  if (pendingThankYouCount && pendingThankYouCount > 0) {
    nudges.push({
      id: "pending-thank-yous",
      title: t('smart-ctas.pending-thanks.title', { count: pendingThankYouCount }),
      description: t('smart-ctas.pending-thanks.description'),
      icon: <Heart className="h-5 w-5" />,
      priority: "medium",
      variant: "action",
      actionLabel: t('common.cta.send-thanks'),
      actionHref: wallets.length > 0 ? `/wallet/${wallets[0].id}` : undefined,
    });
  }

  if (wallets.length > 0 && activePlans.length === 0) {
    nudges.push({
      id: "explore-community",
      title: t('smart-ctas.explore-community.title'),
      description: t('smart-ctas.explore-community.body'),
      icon: <Heart className="h-5 w-5" />,
      priority: "low",
      variant: "info",
      actionLabel: t('common.cta.browse-funds'),
      actionHref: "/community-funds",
    });
  }

  const priorityOrder: Record<NudgePriority, number> = { high: 0, medium: 1, low: 2 };
  nudges.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return nudges;
}

const variantStyles: Record<NudgeVariant, { bg: string; iconBg: string; iconColor: string; border: string }> = {
  success: {
    bg: "bg-muted/40",
    iconBg: "bg-green-100 dark:bg-green-900/40",
    iconColor: "text-green-600 dark:text-green-400",
    border: "border-border",
  },
  warning: {
    bg: "bg-destructive/5",
    iconBg: "bg-amber-100 dark:bg-amber-900/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    border: "border-destructive/20",
  },
  info: {
    bg: "bg-muted/40",
    iconBg: "bg-blue-100 dark:bg-blue-900/40",
    iconColor: "text-blue-600 dark:text-blue-400",
    border: "border-border",
  },
  action: {
    bg: "bg-primary/5",
    iconBg: "bg-primary/10",
    iconColor: "text-primary",
    border: "border-primary/20",
  },
};

export function SmartCTAs({ wallets, transactions, paymentPlans, employerEnrollment, onLinkEmployer, pendingThankYouCount, userCreatedAt }: SmartCTAsProps) {
  const { t } = useTranslation();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const nudges = useMemo(
    () => generateNudges(wallets, transactions, paymentPlans, employerEnrollment, onLinkEmployer, pendingThankYouCount, userCreatedAt, t),
    [wallets, transactions, paymentPlans, employerEnrollment, onLinkEmployer, pendingThankYouCount, userCreatedAt, t]
  );

  const visibleNudges = nudges.filter(n => !dismissedIds.has(n.id));

  if (visibleNudges.length === 0) return null;

  const displayNudges = visibleNudges.slice(0, 3);

  return (
    <Card data-testid="card-smart-ctas">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-amber-500" />
          {t('smart-ctas.suggested-for-you')}
          <Badge variant="secondary">{visibleNudges.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {displayNudges.map((nudge, index) => {
            const styles = variantStyles[nudge.variant];
            return (
              <div
                key={nudge.id}
                className={`relative rounded-md border p-3 sm:p-4 ${styles.bg} ${styles.border} animate-in fade-in slide-in-from-top-1`}
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: "both" }}
                data-testid={`nudge-${nudge.id}`}
              >
                <div className="flex gap-3">
                  <div className={`flex-shrink-0 p-2 rounded-full ${styles.iconBg} ${styles.iconColor}`}>
                    {nudge.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm leading-tight" data-testid={`nudge-title-${nudge.id}`}>
                          {nudge.title}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          {nudge.description}
                        </p>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="flex-shrink-0 opacity-60"
                        onClick={() => setDismissedIds(prev => { const next = new Set(Array.from(prev)); next.add(nudge.id); return next; })}
                        data-testid={`button-dismiss-${nudge.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {nudge.progress !== undefined && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between gap-2 text-xs mb-1">
                          <span className="text-muted-foreground">{nudge.detail}</span>
                          <span className="font-medium">{nudge.progress}%</span>
                        </div>
                        <Progress value={nudge.progress} className="h-1.5" />
                      </div>
                    )}
                    {nudge.actionLabel && (nudge.actionHref || nudge.onAction) && (
                      <div className="mt-2">
                        {nudge.actionHref ? (
                          <Link href={nudge.actionHref}>
                            <Button size="sm" variant="outline" data-testid={`button-action-${nudge.id}`}>
                              {nudge.actionLabel}
                              <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          </Link>
                        ) : (
                          <Button size="sm" variant="outline" onClick={nudge.onAction} data-testid={`button-action-${nudge.id}`}>
                            {nudge.actionLabel}
                            <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {visibleNudges.length > 3 && (
            <p className="text-xs text-center text-muted-foreground" data-testid="text-more-suggestions">
              {t('smart-ctas.more-suggestions', { count: visibleNudges.length - 3 })}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
